import type { ReflowReason } from '@play-co/timestep-core/ui';
import { View } from '@play-co/timestep-core/ui';

/**
 * A symbol used to mark a view that has been wrapped for reflow.
 */
const WRAPPED_REFLOW_FLAG = Symbol('wrappedReflow');

/**
 * Scales a non-flex container's child to fit within its adjusted parent size.
 * If the container has multiple children, they are wrapped in an intermediate container before scaling.
 *
 * @param parentView - The parent view whose child should be scaled.
 * @returns The modified parent view for chaining.
 */
export function scaleToFitParent<
  T extends View & { [WRAPPED_REFLOW_FLAG]?: boolean },
>(parentView: T): T {
  // Ensure the parent has only one child by wrapping multiple children into a single container.
  if (parentView.getSubviews().length > 1) {
    const containerWrapper = new View({
      width: parentView.style.width,
      height: parentView.style.height,
    });

    parentView.getSubviews().forEach((subview) => {
      containerWrapper.addSubview(subview);
    });

    parentView.addSubview(containerWrapper);
  }

  // Retrieve the single child that will be scaled.
  const scaledChild = parentView.getSubviews()[0];

  // No child to scale. Bail.
  if (!scaledChild) return parentView;

  // Check if the parent view has already been wrapped for reflow.
  if (parentView[WRAPPED_REFLOW_FLAG]) return parentView;

  // Mark the parent view as wrapped to prevent double-wrapping.
  parentView[WRAPPED_REFLOW_FLAG] = true;

  // Preserve the original reflow behavior.
  const originalReflow = parentView.reflow;

  // Override reflow to apply scaling.
  parentView.reflow = (reason: ReflowReason) => {
    // Call the original reflow method.
    originalReflow.call(parentView, reason);

    // Only handle flex reflows.
    // reason !== ReflowReason.ComputedUpdate, have to use 2 since ReflowReason is not properly exported.
    if (reason !== 2) return;

    // Calculate the scaling factor to fit the child within the parent while maintaining aspect ratio.
    const scale = Math.min(
      parentView.style.width / scaledChild.style.width,
      parentView.style.height / scaledChild.style.height,
    );

    // Apply scaling and center the child within the parent.
    scaledChild.updateOpts({
      // Use scaleX and scaleY to allow easier animation using the "scale" property.
      scaleX: scale,
      scaleY: scale,
      // Center the child within the parent.
      x: parentView.style.width * 0.5,
      y: parentView.style.height * 0.5,
      centerOnOrigin: true,
    });
  };

  return parentView;
}
